import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import Navbar from '../components/Navbar';
import UserModal from '../components/UserModal';

export default function ManagePage() {
  const [activeTab, setActiveTab] = useState('meters');
  const [meters, setMeters] = useState([]);
  const [users, setUsers] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [editUser, setEditUser] = useState(null);
  const [userNotice, setUserNotice] = useState(null);
  const [mailing, setMailing] = useState(null);
  // Email a user an invitation (to set a password) or a password-reset link
  const sendAccountEmail = async (u, kind) => {
    const what = kind === 'invite' ? 'an invitation to set a password' : 'a password reset link';
    if (!window.confirm(`Email ${what} to ${u.email}?`)) return;
    setMailing(`${kind}:${u.id}`); setUserNotice(null);
    try {
      const r = await axios.post(`/api/users/${u.id}/send-${kind}`, {}, { headers });
      setUserNotice({ ok: true, text: r.data.message });
      fetchAll();
    } catch (err) {
      setUserNotice({ ok: false, text: err.response?.data?.message || 'Could not send the email' });
    } finally { setMailing(null); }
  };
  const [accounts, setAccounts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedMeter, setSelectedMeter] = useState(null);
  const [meterTransactions, setMeterTransactions] = useState([]);
  const [showCreateMeter, setShowCreateMeter] = useState(false);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [showEditMeter, setShowEditMeter] = useState(null);
  const [showTamperCode, setShowTamperCode] = useState(null);
  const [showGenerateToken, setShowGenerateToken] = useState(null);
  const [tamperCode, setTamperCode] = useState('');
  const [generatedToken, setGeneratedToken] = useState('');
  const [newMeter, setNewMeter] = useState({ accountNumber: '', description: '', contactNumber: '', accountId: '', status: 'active' });
  const [editMeter, setEditMeter] = useState({ description: '', contactNumber: '', accountId: '', status: 'active', api_version: 'v1' });
  const [tokenAmount, setTokenAmount] = useState('');
  const [processing, setProcessing] = useState(false);
  const [syncResult, setSyncResult] = useState(null);
  const navigate = useNavigate();

  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const token = localStorage.getItem('token');
  const headers = { Authorization: `Bearer ${token}` };

  const fetchAll = async () => {
    try {
      const [metersRes, accountsRes] = await Promise.all([
        axios.get('/api/meters', { headers }),
        axios.get('/api/accounts', { headers })
      ]);
      setMeters(metersRes.data.meters);
      setAccounts(accountsRes.data.accounts);

      if (user.role === 'admin') {
        const [usersRes, tenantsRes] = await Promise.all([
          axios.get('/api/users', { headers }),
          axios.get('/api/tenants', { headers }),
        ]);
        setUsers(usersRes.data.users);
        setTenants(tenantsRes.data.tenants);
      }
      setLoading(false);
    } catch (err) {
      if (err.response?.status === 401) { localStorage.clear(); navigate('/login'); }
      setError('Failed to load data');
      setLoading(false);
    }
  };

  useEffect(() => { fetchAll(); }, []);

  // Open edit modal with meter data
  const openEditMeter = (m) => {
    setEditMeter({
      description: m.description || '',
      contactNumber: m.contact_number || '',
      accountId: m.account_id || '',
      status: m.status || 'active',
      api_version: m.api_version || 'v1'
    });
    setShowEditMeter(m);
  };

  // Save edited meter
  const handleEditMeter = async () => {
    setProcessing(true);
    try {
      await axios.put(`/api/meters/${showEditMeter.id}`, {
        description: editMeter.description,
        contactNumber: editMeter.contactNumber,
        accountId: editMeter.accountId || null,
        status: editMeter.status,
        api_version: editMeter.api_version
      }, { headers });
      setShowEditMeter(null);
      fetchAll();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update meter');
    } finally { setProcessing(false); }
  };

  // Create meter
  const handleCreateMeter = async () => {
    if (!newMeter.accountNumber.trim()) return;
    setProcessing(true);
    try {
      await axios.post('/api/meters', newMeter, { headers });
      setShowCreateMeter(false);
      setNewMeter({ accountNumber: '', description: '', contactNumber: '', accountId: '', status: 'active' });
      fetchAll();
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to create meter');
    } finally { setProcessing(false); }
  };

  // Sync meters
  const handleSync = async () => {
    setProcessing(true);
    setSyncResult(null);
    try {
      const res = await axios.post('/api/meters/sync', {}, { headers });
      setSyncResult(res.data);
      fetchAll();
    } catch (err) {
      setError('Sync failed — please try again');
    } finally { setProcessing(false); }
  };

  // Toggle API version
  const handleToggleApiVersion = async (m) => {
    const newVersion = m.api_version === 'v2' ? 'v1' : 'v2';
    try {
      await axios.put(`/api/meters/${m.id}/api-version`, { api_version: newVersion }, { headers });
      fetchAll();
    } catch (err) {
      setError('Failed to update API version');
    }
  };

  // Generate tamper code
  const handleTamperCode = async (meter) => {
    setShowTamperCode(meter);
    setTamperCode('');
    setProcessing(true);
    try {
      const res = await axios.post(`/api/meters/${meter.id}/tamper`, {}, { headers });
      setTamperCode(res.data.tamperCode);
    } catch (err) {
      setTamperCode('Failed to generate tamper code');
    } finally { setProcessing(false); }
  };

  // Generate token manually
  const handleGenerateToken = async () => {
    if (!tokenAmount) return;
    setProcessing(true);
    try {
      const res = await axios.post(`/api/meters/${showGenerateToken.id}/token`, { amount: tokenAmount }, { headers });
      setGeneratedToken(res.data.token);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to generate token');
    } finally { setProcessing(false); }
  };

  // View meter transactions
  const handleViewTransactions = async (meter) => {
    setSelectedMeter(meter);
    try {
      const res = await axios.get(`/api/meters/${meter.id}/transactions`, { headers });
      setMeterTransactions(res.data.transactions);
    } catch (err) {
      setError('Failed to load transactions');
    }
  };

  const tabs = [
    { id: 'meters', label: 'Meters', icon: '' },
    ...(user.role === 'admin' ? [{ id: 'users', label: 'Users', icon: '' }] : []),
  ];

  const inputStyle = { width: "100%", boxSizing: "border-box", background: "rgba(255,255,255,0.05)", border: "1.5px solid rgba(56,189,248,0.25)", borderRadius: 10, padding: "11px 14px", color: "#e2e8f0", fontSize: 15, outline: "none", fontFamily: "inherit" };
  const labelStyle = { display: "block", color: "#e2e8f0", fontSize: 12.5, letterSpacing: 1.2, textTransform: "uppercase", marginBottom: 6, fontWeight: 600 };

  return (
    <div style={{ minHeight: "100vh" }}>
      <Navbar user={user} />
      <style>{`
        @keyframes fadeIn { from { opacity: 0; transform: translateY(14px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes spin { to { transform: rotate(360deg); } }
      `}</style>

      <div className="mw-page" style={{ padding: "32px 24px", maxWidth: 1200, margin: "0 auto" }}>

        {/* Header */}
        <div className="mw-page-head" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 32 }}>
          <div>
            <h1 style={{ color: "#0f172a", fontSize: 26, fontWeight: 700, margin: 0 }}>Manage</h1>
            <p style={{ color: "#475569", fontSize: 14, marginTop: 6 }}>Meters, users and accounts</p>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button data-tour="add-meter" onClick={() => setShowCreateMeter(true)} style={{ background: "linear-gradient(135deg, #0891b2, #0e7490)", border: "none", borderRadius: 10, padding: "10px 18px", color: "#fff", fontSize: 14.5, fontWeight: 600, cursor: "pointer" }}>
              + Add Meter
            </button>
            <button data-tour="sync-meters" onClick={handleSync} disabled={processing}
              style={{ background: "#047857", border: "1px solid #047857", borderRadius: 10, padding: "10px 18px", color: "#ffffff", fontSize: 14.5, fontWeight: 600, cursor: processing ? "not-allowed" : "pointer", opacity: processing ? 0.7 : 1 }}>
              {processing ? 'Syncing…' : 'Sync Meters'}
            </button>
            {user.role === 'admin' && (
              <button data-tour="add-user" onClick={() => setShowCreateUser(true)} style={{ background: "#1a5fa4", border: "1px solid #1a5fa4", borderRadius: 10, padding: "10px 18px", color: "#ffffff", fontSize: 14.5, fontWeight: 600, cursor: "pointer" }}>
                + Add User
              </button>
            )}
          </div>
        </div>

        {/* Sync Result */}
        {syncResult && (
          <div style={{ background: "rgba(16,185,129,0.1)", border: "1px solid rgba(16,185,129,0.3)", borderRadius: 10, padding: "14px 18px", color: "#10b981", marginBottom: 24, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <span>Sync complete — Total: <strong>{syncResult.total}</strong> · New: <strong>{syncResult.created}</strong> · Existing: <strong>{syncResult.existing}</strong></span>
            <button onClick={() => setSyncResult(null)} style={{ background: "none", border: "none", color: "#10b981", cursor: "pointer", fontSize: 16 }}>✕</button>
          </div>
        )}

        {/* Error */}
        {error && (
          <div style={{ background: "rgba(248,113,113,0.1)", border: "1px solid rgba(248,113,113,0.3)", borderRadius: 10, padding: "14px 18px", color: "#f87171", marginBottom: 24 }}>
            {error}
            <button onClick={() => setError('')} style={{ float: "right", background: "none", border: "none", color: "#f87171", cursor: "pointer" }}>✕</button>
          </div>
        )}

        {/* Tabs */}
        <div data-tour="manage-tabs" style={{ display: "flex", gap: 4, marginBottom: 24, background: "#0f172a", borderRadius: 12, padding: 4, width: "fit-content" }}>
          {tabs.map(tab => (
            <button key={tab.id} onClick={() => setActiveTab(tab.id)}
              style={{ background: activeTab === tab.id ? "rgba(74,155,224,0.25)" : "none", border: "none", borderRadius: 8, padding: "8px 18px", color: activeTab === tab.id ? "#7cc4ff" : "#e2e8f0", fontSize: 14.5, fontWeight: activeTab === tab.id ? 700 : 500, cursor: "pointer", display: "flex", alignItems: "center", gap: 6 }}>
              {tab.label}
            </button>
          ))}
        </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: "60px 0" }}>
            <div style={{ width: 40, height: 40, borderRadius: "50%", border: "3px solid rgba(56,189,248,0.15)", borderTop: "3px solid #38bdf8", margin: "0 auto 16px", animation: "spin 1s linear infinite" }} />
            <p style={{ color: "#475569" }}>Loading...</p>
          </div>
        ) : (
          <>
            {/* ── METERS TAB ── */}
            {activeTab === 'meters' && (
              <div data-tour="meters-list" style={{ animation: "fadeIn 0.4s ease" }}>
                {selectedMeter ? (
                  <div>
                    <button onClick={() => setSelectedMeter(null)} style={{ background: "none", border: "none", color: "#cbd5e1", cursor: "pointer", fontSize: 15, marginBottom: 20, display: "flex", alignItems: "center", gap: 6 }}>
                      ← Back to Meters
                    </button>
                    <div style={{ background: "rgba(15,23,42,0.92)", borderRadius: 16, border: "1px solid rgba(56,189,248,0.15)", overflow: "hidden" }}>
                      <div style={{ padding: "20px 24px", borderBottom: "1px solid rgba(56,189,248,0.1)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div>
                          <h3 style={{ color: "#ffffff", fontSize: 19, fontWeight: 700, margin: 0 }}>{selectedMeter.account_number}</h3>
                          <p style={{ color: "#cbd5e1", fontSize: 14.5, margin: "4px 0 0" }}>{selectedMeter.description}</p>
                        </div>
                        <div style={{ display: "flex", gap: 8 }}>
                          <button onClick={() => handleTamperCode(selectedMeter)} style={{ background: "rgba(251,191,36,0.1)", border: "1px solid rgba(251,191,36,0.25)", borderRadius: 8, padding: "7px 14px", color: "#fbbf24", fontSize: 13.5, fontWeight: 600, cursor: "pointer" }}>
                            Tamper Code
                          </button>
                          <button onClick={() => { setShowGenerateToken(selectedMeter); setGeneratedToken(''); setTokenAmount(''); }} style={{ background: "rgba(16,185,129,0.1)", border: "1px solid rgba(16,185,129,0.25)", borderRadius: 8, padding: "7px 14px", color: "#10b981", fontSize: 13.5, fontWeight: 600, cursor: "pointer" }}>
                            Generate Token
                          </button>
                        </div>
                      </div>
                      <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                          <thead>
                            <tr style={{ borderBottom: "1px solid rgba(56,189,248,0.1)" }}>
                              {['Date', 'Amount', 'M-Pesa Ref', 'Token', 'Token Used', 'Status'].map(h => (
                                <th key={h} style={{ padding: "12px 20px", textAlign: "left", color: "#cbd5e1", fontSize: 13, letterSpacing: 1.2, textTransform: "uppercase", fontWeight: 700 }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {meterTransactions.length === 0 ? (
                              <tr><td colSpan={6} style={{ padding: "40px", textAlign: "center", color: "#cbd5e1" }}>No transactions yet</td></tr>
                            ) : meterTransactions.map((t) => (
                              <tr key={t.id} style={{ borderBottom: "1px solid rgba(255,255,255,0.04)" }}>
                                <td style={{ padding: "12px 20px", color: "#e2e8f0", fontSize: 13.5 }}>{new Date(t.created_at).toLocaleDateString()}</td>
                                <td style={{ padding: "12px 20px", color: "#10b981", fontSize: 14.5, fontWeight: 600 }}>KES {Number(t.amount).toLocaleString()}</td>
                                <td style={{ padding: "12px 20px", color: "#cbd5e1", fontSize: 13.5 }}>{t.mpesa_ref || '—'}</td>
                                <td style={{ padding: "12px 20px", color: "#38bdf8", fontSize: 13.5, fontFamily: "monospace" }}>{t.token || '—'}</td>
                                <td style={{ padding: "12px 20px" }}>
                                  <span style={{ background: t.token_used ? "rgba(16,185,129,0.1)" : "rgba(248,113,113,0.1)", border: `1px solid ${t.token_used ? "rgba(16,185,129,0.3)" : "rgba(248,113,113,0.3)"}`, borderRadius: 6, padding: "3px 10px", color: t.token_used ? "#10b981" : "#f87171", fontSize: 12.5, fontWeight: 600 }}>
                                    {t.token_used ? 'Used' : 'Unused'}
                                  </span>
                                </td>
                                <td style={{ padding: "12px 20px" }}>
                                  <span style={{ background: t.status === 'completed' ? "rgba(16,185,129,0.1)" : "rgba(248,113,113,0.1)", border: `1px solid ${t.status === 'completed' ? "rgba(16,185,129,0.3)" : "rgba(248,113,113,0.3)"}`, borderRadius: 6, padding: "3px 10px", color: t.status === 'completed' ? "#10b981" : "#f87171", fontSize: 12.5, fontWeight: 600 }}>
                                    {t.status}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div style={{ background: "rgba(15,23,42,0.92)", borderRadius: 16, border: "1px solid rgba(56,189,248,0.15)", overflow: "hidden" }}>
                    <div style={{ padding: "20px 24px", borderBottom: "1px solid rgba(56,189,248,0.1)" }}>
                      <h3 style={{ color: "#ffffff", fontSize: 19, fontWeight: 700, margin: 0 }}>All Meters</h3>
                    </div>
                    {meters.length === 0 ? (
                      <div style={{ padding: "48px", textAlign: "center" }}>
                        <p style={{ color: "#cbd5e1" }}>No meters yet</p>
                        <div style={{ display: "flex", gap: 10, justifyContent: "center", marginTop: 16 }}>
                          <button onClick={() => setShowCreateMeter(true)} style={{ background: "rgba(56,189,248,0.1)", border: "1px solid rgba(56,189,248,0.25)", borderRadius: 8, padding: "8px 20px", color: "#38bdf8", fontSize: 14.5, cursor: "pointer" }}>Add manually</button>
                          <button onClick={handleSync} style={{ background: "rgba(16,185,129,0.1)", border: "1px solid rgba(16,185,129,0.25)", borderRadius: 8, padding: "8px 20px", color: "#10b981", fontSize: 14.5, cursor: "pointer" }}>Sync from external system</button>
                        </div>
                      </div>
                    ) : (
                      <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                          <thead>
                            <tr style={{ borderBottom: "1px solid rgba(56,189,248,0.1)" }}>
                              {['Meter Number', 'Description', 'Account', 'Contact', 'Status', 'API', 'Total Transacted', 'Actions'].map(h => (
                                <th key={h} style={{ padding: "12px 20px", textAlign: "left", color: "#cbd5e1", fontSize: 13, letterSpacing: 1.2, textTransform: "uppercase", fontWeight: 700 }}>{h}</th>
                              ))}
                            </tr>
                          </thead>
                          <tbody>
                            {meters.map((m, i) => (
                              <tr key={m.id} style={{ borderBottom: "1px solid rgba(255,255,255,0.04)", background: i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.01)" }}>
                                <td style={{ padding: "12px 20px", color: "#38bdf8", fontSize: 14.5, fontWeight: 600, fontFamily: "monospace" }}>{m.account_number}</td>
                                <td style={{ padding: "12px 20px", color: "#e2e8f0", fontSize: 14.5 }}>{m.description || '—'}</td>
                                <td style={{ padding: "12px 20px", color: "#e2e8f0", fontSize: 14.5 }}>{m.account_name || <span style={{ color: "#f87171", fontSize: 12.5 }}>No account</span>}</td>
                                <td style={{ padding: "12px 20px", color: "#e2e8f0", fontSize: 14.5 }}>{m.contact_number || '—'}</td>
                                <td style={{ padding: "12px 20px" }}>
                                  <span style={{ background: m.status === 'active' ? "rgba(16,185,129,0.1)" : "rgba(248,113,113,0.1)", border: `1px solid ${m.status === 'active' ? "rgba(16,185,129,0.3)" : "rgba(248,113,113,0.3)"}`, borderRadius: 6, padding: "3px 10px", color: m.status === 'active' ? "#10b981" : "#f87171", fontSize: 12.5, fontWeight: 600 }}>
                                    {m.status}
                                  </span>
                                </td>
                                <td style={{ padding: "12px 20px" }}>
                                  <button onClick={() => handleToggleApiVersion(m)}
                                    title={`Click to switch to ${m.api_version === 'v2' ? 'V1' : 'V2'}`}
                                    style={{ background: m.api_version === 'v2' ? "rgba(16,185,129,0.15)" : "rgba(251,191,36,0.15)", border: `1px solid ${m.api_version === 'v2' ? "rgba(16,185,129,0.4)" : "rgba(251,191,36,0.4)"}`, borderRadius: 6, padding: "3px 10px", color: m.api_version === 'v2' ? "#10b981" : "#fbbf24", fontSize: 12.5, cursor: "pointer", fontWeight: 700 }}>
                                    {m.api_version === 'v2' ? 'V2' : 'V1'}
                                  </button>
                                </td>
                                <td style={{ padding: "12px 20px", color: "#10b981", fontSize: 14.5, fontWeight: 600 }}>KES {Number(m.total_transacted).toLocaleString()}</td>
                                <td style={{ padding: "12px 20px" }}>
                                  <div style={{ display: "flex", gap: 6 }}>
                                    <button onClick={() => openEditMeter(m)} style={{ background: "rgba(56,189,248,0.1)", border: "1px solid rgba(56,189,248,0.25)", borderRadius: 6, padding: "5px 10px", color: "#38bdf8", fontSize: 12.5, cursor: "pointer" }}>Edit</button>
                                    <button onClick={() => handleViewTransactions(m)} style={{ background: "rgba(148,163,184,0.1)", border: "1px solid rgba(148,163,184,0.25)", borderRadius: 6, padding: "5px 10px", color: "#e2e8f0", fontSize: 12.5, cursor: "pointer" }}>History</button>
                                    <button onClick={() => handleTamperCode(m)} style={{ background: "rgba(251,191,36,0.1)", border: "1px solid rgba(251,191,36,0.25)", borderRadius: 6, padding: "5px 10px", color: "#fbbf24", fontSize: 12.5, cursor: "pointer" }}>Tamper</button>
                                    <button onClick={() => { setShowGenerateToken(m); setGeneratedToken(''); setTokenAmount(''); }} style={{ background: "rgba(16,185,129,0.1)", border: "1px solid rgba(16,185,129,0.25)", borderRadius: 6, padding: "5px 10px", color: "#10b981", fontSize: 12.5, cursor: "pointer" }}>Token</button>
                                  </div>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* ── USERS TAB ── */}
            {activeTab === 'users' && userNotice && (
              <div role="status" style={{ display: 'flex', justifyContent: 'space-between', gap: 12, alignItems: 'center', background: userNotice.ok ? '#ecfdf5' : '#fef2f2', border: `1px solid ${userNotice.ok ? '#6ee7b7' : '#fecaca'}`, borderRadius: 10, padding: '12px 16px', color: userNotice.ok ? '#065f46' : '#b91c1c', fontSize: 14.5, fontWeight: 600, marginBottom: 16 }}>
                <span>{userNotice.text}</span>
                <button onClick={() => setUserNotice(null)} aria-label="Dismiss" style={{ background: 'none', border: 'none', color: 'inherit', fontSize: 16, cursor: 'pointer' }}>✕</button>
              </div>
            )}
            {activeTab === 'users' && user.role === 'admin' && (
              <div style={{ animation: "fadeIn 0.4s ease" }}>
                <div style={{ background: "rgba(15,23,42,0.92)", borderRadius: 16, border: "1px solid rgba(56,189,248,0.15)", overflow: "hidden" }}>
                  <div style={{ padding: "20px 24px", borderBottom: "1px solid rgba(56,189,248,0.1)" }}>
                    <h3 style={{ color: "#ffffff", fontSize: 19, fontWeight: 700, margin: 0 }}>All Users</h3>
                  </div>
                  {users.length === 0 ? (
                    <div style={{ padding: "48px", textAlign: "center" }}>
                      <p style={{ color: "#cbd5e1" }}>No users yet</p>
                    </div>
                  ) : (
                    <div style={{ overflowX: "auto" }}>
                      <table style={{ width: "100%", borderCollapse: "collapse" }}>
                        <thead>
                          <tr style={{ borderBottom: "1px solid rgba(56,189,248,0.1)" }}>
                            {['Name', 'Email', 'Role', 'Tenant sites', 'Accounts', 'Created', ''].map(h => (
                              <th key={h} style={{ padding: "12px 20px", textAlign: "left", color: "#cbd5e1", fontSize: 13, letterSpacing: 1.2, textTransform: "uppercase", fontWeight: 700 }}>{h}</th>
                            ))}
                          </tr>
                        </thead>
                        <tbody>
                          {users.map((u, i) => (
                            <tr key={u.id} style={{ borderBottom: "1px solid rgba(255,255,255,0.04)", background: i % 2 === 0 ? "transparent" : "rgba(255,255,255,0.01)" }}>
                              <td style={{ padding: "12px 20px", color: "#e2e8f0", fontSize: 15, fontWeight: 600 }}>
                                {u.name}
                                {u.invite_pending && <span style={{ display: 'block', color: '#fbbf24', fontSize: 12.5, fontWeight: 600 }}>Invite pending{u.invited_at ? ` · sent ${new Date(u.invited_at).toLocaleDateString()}` : ''}</span>}
                              </td>
                              <td style={{ padding: "12px 20px", color: "#e2e8f0", fontSize: 14.5 }}>{u.email}</td>
                              <td style={{ padding: "12px 20px" }}>
                                <span style={{ background: u.role === 'admin' ? "rgba(56,189,248,0.1)" : "rgba(148,163,184,0.1)", border: `1px solid ${u.role === 'admin' ? "rgba(56,189,248,0.3)" : "rgba(148,163,184,0.3)"}`, borderRadius: 6, padding: "3px 10px", color: u.role === 'admin' ? "#38bdf8" : "#e2e8f0", fontSize: 12.5, fontWeight: 600 }}>
                                  {u.role}
                                </span>
                              </td>
                              <td style={{ padding: "12px 20px", color: "#e2e8f0", fontSize: 14.5 }}>
                                {u.role === 'admin' ? <span style={{ color: "#cbd5e1" }}>All</span>
                                  : (u.tenants || []).length ? u.tenants.map(t => t.name).join(', ')
                                  : <span style={{ color: "#cbd5e1" }}>Main site only</span>}
                              </td>
                              <td style={{ padding: "12px 20px", color: "#e2e8f0", fontSize: 14.5 }}>{u.role === 'admin' ? 'All' : u.account_count}</td>
                              <td style={{ padding: "12px 20px", color: "#cbd5e1", fontSize: 13.5 }}>{new Date(u.created_at).toLocaleDateString()}</td>
                              <td style={{ padding: "12px 20px" }}>
                                <button onClick={() => setEditUser(u)} style={{ background: "rgba(56,189,248,0.1)", border: "1px solid rgba(56,189,248,0.25)", borderRadius: 8, padding: "6px 12px", color: "#38bdf8", fontSize: 13.5, fontWeight: 600, cursor: "pointer" }}>
                                  Edit
                                </button>
                                <button onClick={() => sendAccountEmail(u, u.invite_pending ? 'invite' : 'reset')} disabled={!!mailing}
                                  style={{ marginLeft: 6, background: "rgba(251,191,36,0.12)", border: "1px solid rgba(251,191,36,0.4)", borderRadius: 8, padding: "6px 12px", color: "#fbbf24", fontSize: 13.5, fontWeight: 600, cursor: "pointer", whiteSpace: "nowrap" }}>
                                  {mailing === `${u.invite_pending ? 'invite' : 'reset'}:${u.id}` ? 'Sending…' : u.invite_pending ? 'Resend invite' : 'Send password reset'}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            )}
          </>
        )}
      </div>

      {/* ── EDIT METER MODAL ── */}
      {showEditMeter && (
        <div className="mw-modal-overlay" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: 20 }}>
          <div className="mw-modal" style={{ background: "#0f172a", borderRadius: 16, border: "1px solid rgba(56,189,248,0.2)", padding: 32, width: "100%", maxWidth: 480, animation: "fadeIn 0.3s ease" }}>
            <h3 style={{ color: "#ffffff", fontSize: 20, fontWeight: 700, margin: "0 0 6px" }}>Edit Meter</h3>
            <p style={{ color: "#cbd5e1", fontSize: 14.5, margin: "0 0 24px", fontFamily: "monospace" }}>{showEditMeter.account_number}</p>

            {/* Description */}
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Description</label>
              <input type="text" value={editMeter.description}
                onChange={e => setEditMeter(m => ({ ...m, description: e.target.value }))}
                placeholder="e.g. Block A Unit 4" style={inputStyle} />
            </div>

            {/* Contact Number */}
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Contact Number</label>
              <input type="text" value={editMeter.contactNumber}
                onChange={e => setEditMeter(m => ({ ...m, contactNumber: e.target.value }))}
                placeholder="e.g. 0712345678" style={inputStyle} />
            </div>

            {/* Account — dropdown */}
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Assign to Account</label>
              <select value={editMeter.accountId}
                onChange={e => setEditMeter(m => ({ ...m, accountId: e.target.value }))}
                style={{ ...inputStyle }}>
                <option value="">— No account —</option>
                {accounts.map(a => (
                  <option key={a.id} value={a.id}>{a.name}</option>
                ))}
              </select>
              {!editMeter.accountId && (
                <div style={{ color: "#fbbf24", fontSize: 13.5, marginTop: 4 }}>
                  V2 token generation requires an account with a price set
                </div>
              )}
            </div>

            {/* Status */}
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Status</label>
              <select value={editMeter.status}
                onChange={e => setEditMeter(m => ({ ...m, status: e.target.value }))}
                style={{ ...inputStyle }}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>

            {/* API Version */}
            <div style={{ marginBottom: 24 }}>
              <label style={labelStyle}>API Version</label>
              <div style={{ display: "flex", gap: 10 }}>
                {['v1', 'v2'].map(v => (
                  <button key={v} onClick={() => setEditMeter(m => ({ ...m, api_version: v }))}
                    style={{
                      flex: 1, padding: "10px", borderRadius: 10, cursor: "pointer", fontSize: 14.5, fontWeight: 700,
                      background: editMeter.api_version === v ? (v === 'v2' ? "rgba(16,185,129,0.2)" : "rgba(251,191,36,0.2)") : "rgba(255,255,255,0.04)",
                      border: `1px solid ${editMeter.api_version === v ? (v === 'v2' ? "#10b981" : "#fbbf24") : "rgba(255,255,255,0.1)"}`,
                      color: editMeter.api_version === v ? (v === 'v2' ? "#10b981" : "#fbbf24") : "#cbd5e1"
                    }}>
                    {v === 'v2' ? 'V2 — New API' : 'V1 — Old API'}
                  </button>
                ))}
              </div>
              <div style={{ color: "#cbd5e1", fontSize: 12.5, marginTop: 6 }}>
                {editMeter.api_version === 'v2'
                  ? 'V2 requires account with price per unit configured'
                  : 'V1 uses KES amount directly — no account required'}
              </div>
            </div>

            <div style={{ display: "flex", gap: 12 }}>
              <button onClick={() => setShowEditMeter(null)}
                style={{ flex: 1, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10, padding: "12px", color: "#e2e8f0", fontSize: 15, fontWeight: 600, cursor: "pointer" }}>
                Cancel
              </button>
              <button onClick={handleEditMeter} disabled={processing}
                style={{ flex: 1, background: "linear-gradient(135deg, #0891b2, #0e7490)", border: "none", borderRadius: 10, padding: "12px", color: "#fff", fontSize: 15, fontWeight: 600, cursor: "pointer" }}>
                {processing ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── CREATE METER MODAL ── */}
      {showCreateMeter && (
        <div className="mw-modal-overlay" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: 20 }}>
          <div className="mw-modal" style={{ background: "#0f172a", borderRadius: 16, border: "1px solid rgba(56,189,248,0.2)", padding: 32, width: "100%", maxWidth: 440, animation: "fadeIn 0.3s ease" }}>
            <h3 style={{ color: "#ffffff", fontSize: 20, fontWeight: 700, margin: "0 0 24px" }}>Add New Meter</h3>
            {[
              { label: 'Meter Number', key: 'accountNumber', placeholder: 'e.g. 0292000016983' },
              { label: 'Description', key: 'description', placeholder: 'e.g. Block A Unit 4' },
              { label: 'Contact Number', key: 'contactNumber', placeholder: 'e.g. 0712345678' },
            ].map(f => (
              <div key={f.key} style={{ marginBottom: 16 }}>
                <label style={labelStyle}>{f.label}</label>
                <input type="text" value={newMeter[f.key]} onChange={e => setNewMeter(m => ({ ...m, [f.key]: e.target.value }))} placeholder={f.placeholder} style={inputStyle} />
              </div>
            ))}
            <div style={{ marginBottom: 16 }}>
              <label style={labelStyle}>Account</label>
              <select value={newMeter.accountId} onChange={e => setNewMeter(m => ({ ...m, accountId: e.target.value }))} style={{ ...inputStyle }}>
                <option value="">Select account</option>
                {accounts.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
              </select>
            </div>
            <div style={{ marginBottom: 24 }}>
              <label style={labelStyle}>Status</label>
              <select value={newMeter.status} onChange={e => setNewMeter(m => ({ ...m, status: e.target.value }))} style={{ ...inputStyle }}>
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
            <div style={{ display: "flex", gap: 12 }}>
              <button onClick={() => setShowCreateMeter(false)} style={{ flex: 1, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10, padding: "12px", color: "#e2e8f0", fontSize: 15, fontWeight: 600, cursor: "pointer" }}>Cancel</button>
              <button onClick={handleCreateMeter} disabled={processing} style={{ flex: 1, background: "linear-gradient(135deg, #0891b2, #0e7490)", border: "none", borderRadius: 10, padding: "12px", color: "#fff", fontSize: 15, fontWeight: 600, cursor: "pointer" }}>
                {processing ? 'Adding...' : 'Add Meter'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── CREATE / EDIT USER MODAL ── */}
      {(showCreateUser || editUser) && (
        <UserModal
          user={editUser}
          tenants={tenants}
          accounts={accounts}
          headers={headers}
          currentUserId={user.id}
          onClose={() => { setShowCreateUser(false); setEditUser(null); }}
          onSaved={(notice) => { setShowCreateUser(false); setEditUser(null); if (notice) { setUserNotice(notice); setActiveTab('users'); } fetchAll(); }}
        />
      )}

      {/* ── TAMPER CODE MODAL ── */}
      {showTamperCode && (
        <div className="mw-modal-overlay" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: 20 }}>
          <div className="mw-modal" style={{ background: "#0f172a", borderRadius: 16, border: "1px solid rgba(251,191,36,0.2)", padding: 32, width: "100%", maxWidth: 400, animation: "fadeIn 0.3s ease", textAlign: "center" }}>
            <h3 style={{ color: "#ffffff", fontSize: 20, fontWeight: 700, margin: "0 0 8px" }}>Tamper Code</h3>
            <p style={{ color: "#cbd5e1", fontSize: 14.5, margin: "0 0 24px" }}>Meter: {showTamperCode.account_number}</p>
            {processing ? (
              <div style={{ padding: "20px 0" }}>
                <div style={{ width: 32, height: 32, borderRadius: "50%", border: "3px solid rgba(251,191,36,0.15)", borderTop: "3px solid #fbbf24", margin: "0 auto 12px", animation: "spin 1s linear infinite" }} />
                <p style={{ color: "#cbd5e1", fontSize: 14.5 }}>Generating...</p>
              </div>
            ) : (
              <div style={{ background: "rgba(251,191,36,0.08)", border: "1.5px dashed #fbbf24", borderRadius: 12, padding: 20, marginBottom: 24 }}>
                <div style={{ color: "#e2e8f0", fontSize: 12.5, letterSpacing: 2, textTransform: "uppercase", marginBottom: 8 }}>Tamper Code</div>
                <div style={{ fontFamily: "monospace", fontSize: 24, fontWeight: 800, letterSpacing: 4, color: "#fbbf24" }}>{tamperCode}</div>
              </div>
            )}
            <button onClick={() => setShowTamperCode(null)} style={{ width: "100%", background: "rgba(251,191,36,0.1)", border: "1px solid rgba(251,191,36,0.25)", borderRadius: 10, padding: "12px", color: "#fbbf24", fontSize: 15, fontWeight: 600, cursor: "pointer" }}>Close</button>
          </div>
        </div>
      )}

      {/* ── GENERATE TOKEN MODAL ── */}
      {showGenerateToken && (
        <div className="mw-modal-overlay" style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.7)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 200, padding: 20 }}>
          <div className="mw-modal" style={{ background: "#0f172a", borderRadius: 16, border: "1px solid rgba(16,185,129,0.2)", padding: 32, width: "100%", maxWidth: 400, animation: "fadeIn 0.3s ease", textAlign: "center" }}>
            <h3 style={{ color: "#ffffff", fontSize: 20, fontWeight: 700, margin: "0 0 8px" }}>Generate Token</h3>
            <p style={{ color: "#cbd5e1", fontSize: 14.5, margin: "0 0 4px" }}>Meter: {showGenerateToken.account_number}</p>
            <p style={{ color: "#cbd5e1", fontSize: 13.5, margin: "0 0 24px" }}>
              API: <span style={{ color: showGenerateToken.api_version === 'v2' ? "#10b981" : "#fbbf24", fontWeight: 700 }}>
                {showGenerateToken.api_version?.toUpperCase()}
              </span>
              {showGenerateToken.api_version === 'v2' && !showGenerateToken.account_name && (
                <span style={{ color: "#f87171", marginLeft: 8 }}>No account assigned</span>
              )}
            </p>
            {!generatedToken ? (
              <>
                <div style={{ marginBottom: 20, textAlign: "left" }}>
                  <label style={labelStyle}>Amount (KES)</label>
                  <input type="number" value={tokenAmount} onChange={e => setTokenAmount(e.target.value)} placeholder="e.g. 100" style={inputStyle} />
                </div>
                <div style={{ display: "flex", gap: 12 }}>
                  <button onClick={() => setShowGenerateToken(null)} style={{ flex: 1, background: "rgba(255,255,255,0.05)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 10, padding: "12px", color: "#e2e8f0", fontSize: 15, fontWeight: 600, cursor: "pointer" }}>Cancel</button>
                  <button onClick={handleGenerateToken} disabled={processing} style={{ flex: 1, background: "linear-gradient(135deg, #059669, #047857)", border: "none", borderRadius: 10, padding: "12px", color: "#fff", fontSize: 15, fontWeight: 600, cursor: "pointer" }}>
                    {processing ? 'Generating...' : 'Generate'}
                  </button>
                </div>
              </>
            ) : (
              <>
                <div style={{ background: "rgba(16,185,129,0.08)", border: "1.5px dashed #10b981", borderRadius: 12, padding: 20, marginBottom: 24 }}>
                  <div style={{ color: "#e2e8f0", fontSize: 12.5, letterSpacing: 2, textTransform: "uppercase", marginBottom: 8 }}>Token Generated</div>
                  <div style={{ fontFamily: "monospace", fontSize: 20, fontWeight: 800, letterSpacing: 3, color: "#10b981", wordBreak: "break-all" }}>{generatedToken}</div>
                </div>
                <button onClick={() => { setShowGenerateToken(null); setGeneratedToken(''); }} style={{ width: "100%", background: "rgba(16,185,129,0.1)", border: "1px solid rgba(16,185,129,0.25)", borderRadius: 10, padding: "12px", color: "#10b981", fontSize: 15, fontWeight: 600, cursor: "pointer" }}>Done</button>
              </>
            )}
          </div>
        </div>
      )}
    </div>
  );
}